import { useEffect, useRef } from 'react';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import './Banner.css';

const WORDS = ['scale.', 'convert.', 'grow.', 'automate.', 'perform.', 'win.'];

export default function Banner() {
  const wordRef = useRef(null);
  const mWordRef = useRef(null);   // mobile cycling word
  const wordIdx = useRef(0);
  const videoRef = useRef(null);

  // Cycling words animation
  useEffect(() => {
    const els = [wordRef.current, mWordRef.current].filter(Boolean);
    els.forEach(el => { el.textContent = WORDS[0]; });

    const cycle = () => {
      els.forEach(el => el.classList.add('banner-word-out'));
      setTimeout(() => {
        wordIdx.current = (wordIdx.current + 1) % WORDS.length;
        els.forEach(el => {
          el.textContent = WORDS[wordIdx.current];
          el.classList.remove('banner-word-out');
          el.classList.add('banner-word-in');
          setTimeout(() => el.classList.remove('banner-word-in'), 500);
        });
      }, 300);
    };

    const interval = setInterval(cycle, 2200);
    return () => clearInterval(interval);
  }, []);

  // Guaranteed Instant Mobile Autoplay (iOS Safari + Android Chrome)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Direct DOM property enforcement required by WebKit / Android
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('x5-playsinline', '');

    const tryPlay = () => {
      const promise = video.play();
      if (promise !== undefined) {
        promise.catch(() => {
          // If browser policy delays autoPlay, play on the very first touch/scroll gesture
          const handleFirstGesture = () => {
            video.play().catch(() => { });
            window.removeEventListener('touchstart', handleFirstGesture);
            window.removeEventListener('scroll', handleFirstGesture);
            window.removeEventListener('pointerdown', handleFirstGesture);
          };
          window.addEventListener('touchstart', handleFirstGesture, { passive: true, once: true });
          window.addEventListener('scroll', handleFirstGesture, { passive: true, once: true });
          window.addEventListener('pointerdown', handleFirstGesture, { passive: true, once: true });
        });
      }
    };

    // Attempt immediate playback
    tryPlay();

    video.addEventListener('loadeddata', tryPlay, { once: true });
    video.addEventListener('canplay', tryPlay, { once: true });

    return () => {
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
    };
  }, []);

  return (
    <div className="banner-wrap" id="home">

      {/* ── Mobile layout (≤768px) ── */}
      <div className="banner-mobile">
        {/* Full-screen video — baseline faststart + forced DOM muted autoplay */}
        <video
          ref={videoRef}
          className="banner-mobile-video"
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          preload="auto"
          aria-hidden="true"
          poster="/aurelix-logo.png"
        >
          <source src="/hero-mobile.mp4" type="video/mp4" />
          <source src="/Aurelix Website Final Video Compressed.mp4" type="video/mp4" />
        </video>
        {/* Subtle gradient so text pops */}
        <div className="banner-mobile-overlay" aria-hidden="true" />

        {/* Headline — top left */}
        <div className="banner-mobile-top">
          <h1 className="banner-mobile-headline">
            your brand<br />can{' '}
            <span className="banner-word" ref={mWordRef}>scale.</span>
          </h1>
        </div>

        {/* Description + CTA — bottom right */}
        <div className="banner-mobile-bottom">
          <p className="banner-mobile-sub">
            We connect your ads, funnels, and data into one system — so you always know what's working.
          </p>
          <AntiMetalButton as="a" href="mailto:hello@aurelixlabs.ai">Get in touch</AntiMetalButton>
        </div>
      </div>

      {/* ── Desktop layout (>768px) ── */}
      <div className="banner-inner">
        <p className="banner-eyebrow">Performance Marketing · Data Analytics · AI Automation</p>
        <h1 className="banner-headline">
          your brand can{' '}
          <span className="banner-word" ref={wordRef}>scale.</span>
        </h1>
        <p className="banner-sub">
          We connect your ads, funnels, and data into one system — so you always know what's working.
        </p>
        <div className="banner-actions">
          <AntiMetalButton as="a" href="mailto:hello@aurelixlabs.ai">Get in touch</AntiMetalButton>
          <AntiMetalButton as="a" href="/services" accentFrom="#444" accentTo="#222">Our services →</AntiMetalButton>
        </div>
      </div>

    </div>
  );
}
